import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '@environment/environment';
import {catchError, retry} from "rxjs/operators";
import {Observable, throwError, finalize, timer, MonoTypeOperatorFunction} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {HttpHeaders} from '@angular/common/http';
import {LoaderService} from '@services/loader/loader.service';
import {MessagesService} from '@services/messages/messages.service';
import {ApiResponse} from '@interfaces/response';
import {RequestOptions} from '@interfaces/request_options';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private loadingService: LoaderService = inject(LoaderService);
  private messageService: MessagesService = inject(MessagesService);
  private translate: TranslateService = inject(TranslateService);

  /**
   * Contiene URL di base per le chiamate REST
   * @var {string}
   */
  private url: string = environment.api.base_url;

  /**
   * Costruttore del service
   *
   * @param HttpClient http effettua le chiamate REST
   * @param Config config gestisce i dati di configurazione
   */
  private http = inject(HttpClient);

  /**
   * Effettua la chiamata GET
   * @param string endpoint url (parte finale) dove effettuare
   * @param array params parametri (key:value) da passare alla chiamata GET
   * @param HttpParams reqOpts  parametri da utilizzare nell'intestazione
   * @return Observable
   */
  get<T>(endpoint: string, params?: any, reqOpts?: RequestOptions, loaderShow = true): Observable<any> {
    if (loaderShow) {
      this.loadingService.show();
    }

    if (!reqOpts) {
      reqOpts = {};
    }

    if (params) {
      let httpParams = new HttpParams();
      for (const k in params) {
        if (params.hasOwnProperty(k)) {
          httpParams = httpParams.set(k, params[k]);
        }
      }
      reqOpts.params = httpParams;
    }
    return this.http.get<ApiResponse<T>>(this.url + endpoint, reqOpts).pipe(
      this.riprova(false),
      catchError(this.handleError),
      finalize(() => this.loadingService.hide())
    );
  }

  getExternalUrl(endpoint: string, params?: any, reqOpts?: RequestOptions, loaderShow = true) {
    if (loaderShow) {
      this.loadingService.show();
    }
    if (!reqOpts) {
      reqOpts = {
        params: new HttpParams()
      };
    }
    if (params) {
      reqOpts.params = new HttpParams();
      for (const k of params) {
        reqOpts.params = reqOpts.params.set(k, params[k]);
      }
    }
    return this.http.get(endpoint, reqOpts).pipe(this.riprova(false), catchError(this.handleError))
  }

  /**
   * Effettua la chiamata POST
   * @param string endpoint url (parte finale) dove effettuare
   * @param JSON body contiene i valori da passare alla chiamata in formato JSON
   * @param HttpParams reqOpts  parametri da utilizzare nell'intestazione
   * @return Observable
   */
  post<T>(endpoint: string, body: any, params?: any, reqOpts?: RequestOptions): Observable<any> {
    this.loadingService.show();
    if (!reqOpts) {
      reqOpts = {
        params: new HttpParams()
      };
    }
    if (params) {
      reqOpts.params = new HttpParams();
      for (const k in params) {
        reqOpts.params = reqOpts.params.set(k, params[k]);
      }
    }
    return this.http.post<ApiResponse<T>>(this.url + endpoint, body, {
      ...reqOpts,
      observe: 'body',
      responseType: 'json'
    }).pipe(
      this.riprova(true),
      catchError(this.handleError.bind(this)),
      finalize(() => this.loadingService.hide())
    );
  }

  /**
   * Effettua la chiamata PUT
   * @param string endpoint url (parte finale) dove effettuare
   * @param JSON} body contiene i valori da passare alla chiamata in formato JSON
   * @param HttpParams reqOpts  parametri da utilizzare nell'intestazione
   * @return Observable
   */
  put<T>(endpoint: string, body: any, params?: any, reqOpts?: RequestOptions): Observable<any> {
    this.loadingService.show();
    if (!reqOpts) {
      reqOpts = {
        params: new HttpParams()
      };
    }
    if (params) {
      reqOpts.params = new HttpParams();
      for (const k of params) {
        reqOpts.params = reqOpts.params.set(k, params[k]);
      }
    }
    return this.http.put<ApiResponse<T>>(this.url + endpoint, body, reqOpts).pipe(this.riprova(true), catchError(this.handleError.bind(this)),
      finalize(() => this.loadingService.hide()))
  }

  /**
   * Effettua la chiamata DELETE
   * @param string endpoint url (parte finale) dove effettuare
   * @param JSON body contiene i valori da passare alla chiamata in formato JSON
   * @param HttpParams reqOpts  parametri da utilizzare nell'intestazione
   * @return Observable
   */
  delete<T>(endpoint: string, reqOpts?: RequestOptions): Observable<any> {
    this.loadingService.show();
    return this.http.delete<ApiResponse<T>>(this.url + endpoint, reqOpts).pipe(this.riprova(true), catchError(this.handleError.bind(this)),
      finalize(() => this.loadingService.hide()))
  }

  /**
   * Effettua la chiamata PATCH
   * @param string endpoint url (parte finale) dove effettuare
   * @param JSON body contiene i valori da passare alla chiamata in formato JSON
   * @param HttpParams reqOpts  parametri da utilizzare nell'intestazione
   * @return Observable
   */
  patch<T>(endpoint: string, body: any, reqOpts?: RequestOptions): Observable<any> {
    this.loadingService.show();
    return this.http.patch<ApiResponse<T>>(this.url + endpoint, body, reqOpts).pipe(this.riprova(true), catchError(this.handleError.bind(this)),
      finalize(() => this.loadingService.hide()))
  }


  /**
   * Prima ogni chiamata fallita veniva ripetuta (retry(1)), anche un login sbagliato (che contava due tentativi
   * contro il limite dell'API) o un invio di email. Ora non si ripetono mai gli errori 4xx; le letture si ripetono
   * se il server non risponde, le scritture solo con 502 (API in riavvio: la richiesta non è arrivata).
   */
  private riprova<R>(scrittura: boolean): MonoTypeOperatorFunction<R> {
    return retry({
      count: 1,
      delay: (err: any) => {
        const s = err?.status;
        const ripetibile = scrittura ? s === 502 : (s === 0 || s >= 502);
        return ripetibile ? timer(1000) : throwError(() => err);
      }
    });
  }

  /** Chiave di alert.* da mostrare: sempre un messaggio tradotto, mai "Error Code: 500 Message: Http failure…". */
  private chiaveErrore(error: any): string {
    const status = error?.status;
    const codice = error?.error?.code_error;
    const messaggio = error?.error?.message;
    if (status === 429) {
      return 'too_many_attempts';
    }
    if (codice === 'email_not_sent') {
      return 'email_not_sent';
    }
    if (codice === 'validation_error') {
      // "Link di reset scaduto" vale solo per la reimpostazione della password; altrove è un dato non valido
      return String(error?.url || '').includes('/auth/password/reset/confirm/') ? 'validation_error' : 'invalid_data';
    }
    if (typeof messaggio === 'string' && messaggio && this.translate.instant(`alert.${messaggio}`) !== `alert.${messaggio}`) {
      return messaggio;
    }
    if (status === 0) {
      return 'server_unreachable';
    }
    if (status >= 500) {
      return 'server_error';
    }
    return 'generic_error';
  }

  private handleError = (error: any) => {
    const chiave = this.chiaveErrore(error);
    if (this.messageService) {
      this.messageService.error('error', chiave, 5000);
    }
    return throwError(() => this.translate.instant(`alert.${chiave}`));
  }

}
